import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  where,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate, Link } from "react-router-dom";

export default function ConsentLogs() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [bookingIdFilter, setBookingIdFilter] = useState("");
  const [renterFilter, setRenterFilter] = useState(""); // uid or email partial
  const [tierFilter, setTierFilter] = useState("any");
  const [sinceDays, setSinceDays] = useState(30);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchLogs() {
    setLoading(true);
    try {
      // basic: last N days, newest first
      const since = new Date();
      since.setDate(since.getDate() - Number(sinceDays || 30));

      let q = query(
        collection(db, "consentLogs"),
        where("createdAt", ">=", since),
        orderBy("createdAt", "desc"),
        limit(200)
      );

      const snap = await getDocs(q);
      const items = [];
      snap.forEach(doc => items.push({ id: doc.id, ...doc.data() }));
      setRows(items);
    } catch (e) {
      console.error("Failed to fetch consent logs", e);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (tierFilter !== "any" && (r.protectionTier || "").toLowerCase() !== tierFilter) return false;
      if (bookingIdFilter && !String(r.bookingId || "").includes(bookingIdFilter.trim())) return false;
      if (renterFilter) {
        const needle = renterFilter.trim().toLowerCase();
        const emailHit = (r.renterEmail || "").toLowerCase().includes(needle);
        const uidHit = (r.renterUid || "").toLowerCase().includes(needle);
        if (!emailHit && !uidHit) return false;
      }
      return true;
    });
  }, [rows, tierFilter, bookingIdFilter, renterFilter]);

  function fmtMoney(v) {
    if (v == null) return "-";
    return `$${Number(v).toFixed(2)}`;
    // depositAmount is already dollars in the iOS payload
  }

  function exportCSV() {
    const headers = [
      "createdAtISO",
      "bookingId",
      "listingId",
      "renterUid",
      "renterEmail",
      "protectionTier",
      "depositAmount",
      "planCap",
      "maxCapApplied",
      "agreementVersion",
      "agreementUrl",
      "consentTextHash",
      "appVersion",
      "appPlatform",
      "docId",
    ];

    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const created = r.createdAt?.toDate ? r.createdAt.toDate().toISOString() : "";
      const row = [
        created,
        safe(r.bookingId),
        safe(r.listingId),
        safe(r.renterUid),
        safe(r.renterEmail),
        safe(r.protectionTier),
        r.depositAmount ?? "",
        r.planCap ?? "",
        r.maxCapApplied ?? "",
        safe(r.agreementVersion),
        safe(r.agreementUrl),
        safe(r.consentTextHash),
        safe(r.appVersion),
        safe(r.appPlatform),
        safe(r.id),
      ].join(",");
      lines.push(row);
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `consent_logs_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function safe(v) {
    if (v == null) return "";
    const s = String(v).replaceAll(/"/g, '""');
    return `"${s}"`;
  }

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/dashboard")} style={styles.back}>
        ← Back to Dashboard
      </button>

      <h2 style={styles.title}>🧾 Consent Logs</h2>

      <div style={styles.filters}>
        <label>
          Booking ID:
          <input value={bookingIdFilter} onChange={(e)=>setBookingIdFilter(e.target.value)} style={styles.input}/>
        </label>
        <label>
          Renter (uid/email):
          <input value={renterFilter} onChange={(e)=>setRenterFilter(e.target.value)} style={styles.input}/>
        </label>
        <label>
          Tier:
          <select value={tierFilter} onChange={(e)=>setTierFilter(e.target.value)} style={styles.input}>
            <option value="any">Any</option>
            <option value="none">None</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
          </select>
        </label>
        <label>
          Since (days):
          <input type="number" min="1" value={sinceDays} onChange={(e)=>setSinceDays(e.target.value)} style={{...styles.input, width:100}}/>
        </label>
        <button onClick={fetchLogs} disabled={loading} style={styles.btn}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <button onClick={exportCSV} style={{...styles.btn, background:"#0ea5e9"}}>
          Export CSV
        </button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th>Created</th>
              <th>Booking</th>
              <th>Renter</th>
              <th>Tier</th>
              <th>Cap</th>
              <th>Deposit</th>
              <th>Max Cap</th>
              <th>Hash</th>
              <th>Open</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const created = r.createdAt?.toDate ? r.createdAt.toDate().toLocaleString() : "";
              return (
                <tr key={r.id}>
                  <td>{created}</td>
                  <td>{r.bookingId}</td>
                  <td>
                    <div style={{display:"flex", flexDirection:"column"}}>
                      <span title={r.renterUid}>{r.renterEmail || r.renterUid || "-"}</span>
                      <small style={{opacity:0.6}}>{r.renterUid}</small>
                    </div>
                  </td>
                  <td>{r.protectionTier || "-"}</td>
                  <td>${(r.planCap ?? 0).toFixed?.(2) ?? Number(r.planCap||0).toFixed(2)}</td>
                  <td>{fmtMoney(r.depositAmount)}</td>
                  <td>${(r.maxCapApplied ?? 0).toFixed?.(2) ?? Number(r.maxCapApplied||0).toFixed(2)}</td>
                  <td>
                    <code title={r.consentTextHash}>{String(r.consentTextHash || "").slice(0,8)}…</code>
                  </td>
                  <td>
                    <Link to={`/bookings?bookingId=${encodeURIComponent(r.bookingId || "")}`}>Booking</Link>
                    {" · "}
                    <Link to={`/deposit-tool?bookingId=${encodeURIComponent(r.bookingId || "")}`}>Deposit</Link>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={9} style={{textAlign:"center", padding:"1rem"}}>No logs</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  container: { padding:"2rem", fontFamily:"system-ui, sans-serif", background:"#f9f9f9", minHeight:"100vh" },
  back: { background:"#eee", border:"1px solid #ccc", padding:"0.4rem 0.8rem", borderRadius:6, cursor:"pointer", marginBottom:"1rem" },
  title: { color:"#065f46", margin:"0 0 1rem" },
  filters: { display:"flex", flexWrap:"wrap", gap:"0.75rem", alignItems:"center", marginBottom:"1rem" },
  input: { padding:"0.4rem 0.6rem", border:"1px solid #ccc", borderRadius:6 },
  btn: { background:"#22c55e", color:"#fff", border:"none", borderRadius:6, padding:"0.45rem 0.9rem", cursor:"pointer", fontWeight:600 },
  tableWrap: { overflowX:"auto", background:"#fff", borderRadius:10, boxShadow:"0 2px 6px rgba(0,0,0,0.08)" },
  table: { width:"100%", borderCollapse:"collapse" },
  thtd: { padding:"0.6rem 0.8rem" }
};
